import React, { useEffect, useMemo, useRef, useState } from "react";
import { Image, Line, Circle, Text, Rect } from "react-konva";
import { Annotation, BBoxPixels, SegmentationData } from "@/types/annotations";
import { KonvaEventObject } from "konva/lib/Node";
import { measureDistanceCm, isDisplayPointInMask, type MaskContext } from "@/lib/measurement/depth3d";
import type { MeasurementEditingMode } from "@/lib/measurement/depthAvailability";
import { decodeCompressedRLEToMask } from "@/utils/rleUtils";
import { zoomAdjustedLayerSize } from "../AnnotationViewerBase/helpers";
import {
  maskToCanvas,
  isManualBBoxAnnotation,
  isMeasurementAxisValued,
  UNMEASURED_AXIS_DASH,
  UNMEASURED_AXIS_STROKE,
} from "./helpers";
import {
  buildAnnotationMeasurementLabels,
  buildMeasurementLabelLayout,
  buildMeasurementLineDrawSpecs,
  describeMeasurementLineEndpoint,
  getContrastingOutlineColor,
  MEASUREMENT_LABEL_CHAR_WIDTH_RATIO,
  type MeasurementLineDrawSpec,
  MEASUREMENT_LABEL_LINE_HEIGHT,
} from "@/utils/annotationMeasurementUtils";
import {
  parseMeasurementEndpointName,
  withMeasurementAxisValue,
  type MeasurementAxisEndpointKind,
  type MeasurementDescriptor,
} from "@shared/types/measurementDescriptors";
import {
  isUserAdjustedMeasurementKeypoint,
  type BoxKeypointUpdate,
  type MeasurementKeypointName,
} from "./measurementEdit";
import { classifyBoxChange, transformAxisForBoxChange, type Axis } from "@/lib/measurement/keypointBoxTransform";
import { seedAxisForKey } from "@shared/types/measurementKeypointPlacement";

const LABEL_FONT_SIZE = 40;
const CIRCLE_RADIUS = 8;
const LABEL_PADDING = 8;
const MIN_BOX_SIZE = 6;
// Collapse-to-points mode: each box becomes a single center dot. Larger when the
// dot is the one a double-click would toggle (hovered/selected), mirroring the box highlight.
const COLLAPSE_POINT_RADIUS_PX = 6;
const COLLAPSE_POINT_ACTIVE_RADIUS_PX = 9.5;
/** Faint enough to read as "parked", solid enough to prove the data is still there. */
const HIDDEN_AXIS_OPACITY = 0.2;

// Default treatment (box NOT active for editing): the original subtle chrome, in raw layer units divided
// by the stage zoom — i.e. how the lines/dots looked before the editing-emphasis pass.
const AXIS_LINE_WIDTH = 8;
const BOX_OUTLINE_WIDTH = 6;
const BOX_OUTLINE_EMPHASIS_WIDTH = 7;

// Active-for-editing treatment (box hovered or selected in edit mode): a modestly more prominent,
// resolution-independent look — a contrasting casing under each line and a contrasting ring on each dot —
// kept restrained so it aids precision without dominating. `zoomAdjustedLayerSize` keeps them screen-stable.
const ACTIVE_AXIS_LINE_BASE_PX = 2.5;
const ACTIVE_AXIS_LINE_CASING_EXTRA_PX = 2.5;
const ACTIVE_KEYPOINT_RADIUS_PX = 4.5;
const ACTIVE_KEYPOINT_STROKE_PX = 1.5;
// Draggable endpoint handle: a fixed on-screen size (growth 0) so it stays a stable grab target.
const ACTIVE_HANDLE_RADIUS_PX = 6;
const ACTIVE_HANDLE_STROKE_PX = 2.5;
/** How a resize corner reads under the cursor. It has to survive the crosshair guides
 *  crossing straight over it, so growth alone is not enough — the ring thickens too —
 *  while staying small enough that the corner does not appear to move.
 *  Raw layer units like `AXIS_LINE_WIDTH` (divided by the stage zoom at use), NOT the
 *  `_PX` screen-space family. */
const RESIZE_HANDLE_HOVER_GROWTH = 1.6;
const RESIZE_HANDLE_RING_WIDTH = 2.5;
const RESIZE_HANDLE_HOVER_RING_WIDTH = 4;
/** Hover casing, as a fraction of the handle's own radius — so it scales with the
 *  handle instead of swamping it on a high-resolution capture, where the corner is
 *  only a couple of screen pixels across. */
const RESIZE_HANDLE_HOVER_CASING = 0.8;
/** Grab/hover target around a handle. Also the radius that decides whether a second
 *  landmark is close enough to be indistinguishable — one constant so the callout can
 *  never claim a neighbour is reachable when the hit test disagrees. */
const HANDLE_HIT_STROKE_PX = 32;
const HANDLE_HIT_STROKE_MIN_PX = 16;

// Landmark name callout: shown on hover (or when the sidebar legend points at a
// landmark), never persistently — a name over every dot would bury the imagery.
// All in on-screen px; `screenPx` maps them into Layer units.
const CALLOUT_TITLE_FONT_PX = 12;
const CALLOUT_SUBTITLE_FONT_PX = 10;
const CALLOUT_LINE_GAP_PX = 2;
const CALLOUT_PADDING_PX = 6;
const CALLOUT_CORNER_RADIUS_PX = 4;
/** Clearance between the dot and the callout, so the chip never covers the point it names. */
const CALLOUT_OFFSET_PX = 12;
const CALLOUT_RING_EXTRA_PX = 4;
const CALLOUT_RING_STROKE_PX = 2;
const CALLOUT_BACKGROUND = "rgba(15, 23, 42, 0.92)";
const CALLOUT_SUBTITLE_COLOR = "#cbd5e1";

type ResizeCorner = "nw" | "ne" | "se" | "sw";

/**
 * The dark casing that marks a handle as grabbable. Shared so the resize corners and
 * the measurement landmarks cannot drift apart — the corner's comment claims they
 * match, and nothing else would enforce it. `blur` is in the caller's own units.
 */
function grabbableCasing(active: boolean, blur: number) {
  return {
    shadowColor: active ? "#111827" : undefined,
    shadowBlur: active ? blur : 0,
    shadowOpacity: active ? 0.9 : 0,
  };
}

/**
 * Locate a landmark by its persisted name within a spec list, returning which end
 * of which axis it is. One definition of "a landmark is identified by matching
 * either end's name", shared by the drag path and the name callout — which read
 * different spec arrays (stored vs draft-adjusted) and would otherwise drift.
 */
function findEndpointByName(
  specs: readonly MeasurementLineDrawSpec[],
  name: string
): { spec: MeasurementLineDrawSpec; endpoint: MeasurementAxisEndpointKind } | null {
  for (const spec of specs) {
    if (spec.start.name === name) return { spec, endpoint: "start" };
    if (spec.end.name === name) return { spec, endpoint: "end" };
  }
  return null;
}

/** No editing mode supplied means the capture's depth is unresolved: locked. */
const LOCKED_MEASUREMENT_EDITING: MeasurementEditingMode = { kind: "locked" };

export type MeasurementDragDraft = {
  annotationId: string;
  keypointName: MeasurementKeypointName;
  axisKey: string;
  /** The full landmark name, e.g. "Core Length — End". Composed by
   *  `describeMeasurementLineEndpoint` so the HUD, the canvas callout, and the
   *  sidebar legend cannot word the same landmark differently — naming only the
   *  axis would leave a labeler unable to tell the base from the tip. */
  label: string;
  /** The dragged DOT's colour, so the HUD accent matches the handle under the cursor. */
  color: string;
  valueCm: number | null;
  /** Set instead of `valueCm` in 2D mode, where only on-screen geometry is knowable. */
  pixelLengthPx?: number;
  x: number;
  y: number;
  bbox?: BBoxPixels;
};

type SegmentationViewerProps = {
  annotation: Annotation;
  color: string;
  outlineColor?: string;
  /** Review-only styling for boundary-hidden detections; never changes saved data. */
  outsideBoundaryCandidateState?: "pending" | "rejected";
  totalScale: number;
  fitScale?: number;
  isVisible: boolean;
  showText?: boolean;
  /** View-only: hide the mask fill while keeping keypoints/lines. Never affects downloads. */
  showMask?: boolean;
  /** Edit-only declutter: draw only a center dot, dropping mask/box/lines/keypoints. */
  collapseToPoints?: boolean;
  isEditMode?: boolean;
  selected?: boolean;
  isHovered?: boolean;
  suppressHoverPreview?: boolean;
  rawWidth: number;
  rawHeight: number;
  measurementDescriptors?: MeasurementDescriptor[];
  /** Axis keys the user toggled off in the sidebar. Dimmed and non-interactive so
   *  a coincident handle underneath can be grabbed. View-only: never persisted. */
  hiddenMeasurementKeys?: ReadonlySet<string>;
  /** The landmark the sidebar legend is pointing at, or null. Narrowed to a single
   *  annotation by the container — same shape as `selected` — so a bare name can
   *  never ring that landmark on every box in the image. */
  highlightedKeypointName?: string | null;
  /** The image-space rectangle currently on screen, so a hover callout can be kept
   *  readable rather than merely inside the image. A getter, not a value: it is read
   *  only while a callout exists, and a prop would re-render every box on every pan
   *  frame. */
  getVisibleImageRect?: () => { x: number; y: number; width: number; height: number } | null;
  /** What this capture's depth evidence allows: 3D recompute while dragging,
   *  2D position-only editing, or (the default) no endpoint editing at all. */
  measurementEditing?: MeasurementEditingMode;
  onSelect?: () => void;
  onToggleAtPoint?: (point: { x: number; y: number }) => void;
  onBBoxChange?: (bbox: BBoxPixels, update: BoxKeypointUpdate) => void;
  onMeasurementKeypointChange?: (
    keypointName: MeasurementKeypointName,
    x: number,
    y: number,
    recomputedMeters: number | null
  ) => void;
  onMeasurementDraft?: (draft: MeasurementDragDraft | null) => void;
  onInvalidMeasurement?: () => void;
};

export const SegmentationViewer = React.memo(
  ({
    annotation,
    color,
    outlineColor,
    outsideBoundaryCandidateState,
    totalScale,
    fitScale = 1,
    isVisible,
    showText = true,
    showMask = true,
    collapseToPoints = false,
    isEditMode,
    selected,
    isHovered,
    suppressHoverPreview,
    rawWidth,
    rawHeight,
    measurementDescriptors = [],
    hiddenMeasurementKeys,
    highlightedKeypointName,
    getVisibleImageRect,
    measurementEditing = LOCKED_MEASUREMENT_EDITING,
    onSelect,
    onToggleAtPoint,
    onBBoxChange,
    onMeasurementKeypointChange,
    onMeasurementDraft,
    onInvalidMeasurement,
  }: SegmentationViewerProps) => {
    const { bbox, segmentation, keypoints, measurements } = annotation;
    // Skip the RLE decode + per-pixel canvas build entirely when collapsed to a point —
    // the collapse branch never draws the mask, so decoding it is pure waste (collapse
    // mode exists to make crowded images lighter to render).
    const maskCanvas = useMemo(
      () => (!collapseToPoints && bbox && segmentation ? maskToCanvas(bbox, segmentation, color) : null),
      [bbox, segmentation, color, collapseToPoints]
    );
    // A toggled-off axis stays drawn — faint — so the user can tell the measurement
    // is still recorded, just parked. Being non-listening is what frees a coincident
    // handle underneath it to be grabbed.
    const isAxisHidden = (axisKey: string) => Boolean(hiddenMeasurementKeys?.has(axisKey));
    // Hover is driven by the parent's closest-to-center hit-testing, so the highlighted box is exactly the
    // one a double-click would toggle. Single source of truth for the "hover preview is live" rule.
    const showHoverPreview = isEditMode && !suppressHoverPreview && Boolean(isHovered);
    // The box the user is working with: hovered (edit mode) or selected. Drives the emphasized line/dot
    // treatment AND makes its endpoint handles grabbable directly on hover — so the user no longer has to
    // click the box to select it before they can grab a point.
    const isActiveForEditing = Boolean((isEditMode && selected) || showHoverPreview);
    // Collapse mode shows only points; no endpoint editing (and no mask decode for it).
    const endpointsEditable = Boolean(
      isActiveForEditing &&
        isVisible &&
        onMeasurementKeypointChange &&
        measurementEditing.kind !== "locked" &&
        !collapseToPoints
    );
    // Naming a landmark is a READ, so it deliberately drops `endpointsEditable`'s
    // depth and handler preconditions: a depth-locked capture — every one whose
    // depth map never arrived — still has to say which point is which.
    const endpointsIdentifiable = Boolean(isActiveForEditing && isVisible && !collapseToPoints);
    /**
     * Weaker still: telling a landmark's role by COLOUR needs no active box, because
     * the sidebar legend is on screen for every box from the moment edit mode opens.
     * So each landmark carries its own colour throughout editing — a labeler has to
     * be able to tell `core_length_start` from `core_length_end`, since the pipeline
     * learns them by name and a swapped pair trains wrong while looking right.
     *
     * Preview keeps BOTH endpoints on the axis colour, because that is what the
     * downloaded annotated JPEG draws (`annotationRenderingService.drawMeasurementAxes`)
     * and the two must keep agreeing.
     */
    const endpointsColorCoded = Boolean(isEditMode);
    const shouldDecodeMeasurementMask = Boolean(bbox && segmentation && endpointsEditable);
    // Decoding the RLE mask is only needed while editing endpoints, but hover now flips
    // `shouldDecodeMeasurementMask` on/off as the cursor moves. Cache the decoded grid per segmentation
    // identity so each mask decodes at most once instead of re-decoding on every hover-enter.
    const maskGridCacheRef = useRef<{ segmentation: SegmentationData; grid: boolean[][] } | null>(null);
    const maskGrid = useMemo(() => {
      if (!shouldDecodeMeasurementMask || !segmentation) return null;
      if (maskGridCacheRef.current?.segmentation !== segmentation) {
        maskGridCacheRef.current = { segmentation, grid: decodeCompressedRLEToMask(segmentation.compressedRLE) };
      }
      return maskGridCacheRef.current.grid;
    }, [segmentation, shouldDecodeMeasurementMask]);
    const maskContext: MaskContext | null = maskGrid && bbox ? { grid: maskGrid, bbox } : null;
    const measurementLineSpecs = useMemo(() => buildMeasurementLineDrawSpecs(keypoints), [keypoints]);
    // Which landmark the cursor is over, so its name can be shown. Hover-only by
    // design: a persistent name over every dot would bury the imagery the labeler
    // is judging.
    const [hoveredKeypointName, setHoveredKeypointName] = useState<string | null>(null);
    /** Which resize corner the cursor is over, so it can read as the grabbable one. */
    const [hoveredResizeCorner, setHoveredResizeCorner] = useState<ResizeCorner | null>(null);
    /** The stage container this viewer last wrote a cursor onto. See `setStageCursor`. */
    const cursorContainerRef = useRef<HTMLDivElement | null>(null);

    const [draftBBox, setDraftBBoxState] = useState<BBoxPixels | null>(null);
    const draftBBoxRef = useRef<BBoxPixels | null>(null);

    // While the user drags one measurement endpoint we keep its live position in
    // a draft so the line + label update continuously without committing.
    const [draftKeypoint, setDraftKeypointState] = useState<{
      name: MeasurementKeypointName;
      axisKey: string;
      x: number;
      y: number;
      valueCm: number | null;
    } | null>(null);
    const draftKeypointRef = useRef<typeof draftKeypoint>(null);
    const setDraftKeypoint = (next: typeof draftKeypoint) => {
      draftKeypointRef.current = next;
      setDraftKeypointState(next);
    };

    // While the box is being dragged its landmarks must move with it on screen,
    // or the handles visibly detach from the box until the drag is released.
    const boxChange = bbox && draftBBox ? classifyBoxChange(bbox, draftBBox) : null;
    // Derived from the axis key itself, never from the trait descriptors: a
    // persisted axis the current config does not define is still a real axis,
    // and gating on descriptors would drop it at the image origin.
    const seedFallbackFor = (key: string, index: number): Axis => seedAxisForKey(key, draftBBox ?? bbox!, index);
    const transformedSpecs = boxChange
      ? measurementLineSpecs.map((spec, specIndex) => {
          const moved = transformAxisForBoxChange(
            { start: { x: spec.start.x, y: spec.start.y }, end: { x: spec.end.x, y: spec.end.y } },
            boxChange,
            seedFallbackFor(spec.key, specIndex),
            { width: rawWidth, height: rawHeight }
          );
          return { ...spec, start: { ...spec.start, ...moved.start }, end: { ...spec.end, ...moved.end } };
        })
      : measurementLineSpecs;

    const activeMeasurementLineSpecs = transformedSpecs.map((spec) => ({
      ...spec,
      start:
        draftKeypoint?.name === spec.start.name
          ? { ...spec.start, x: draftKeypoint.x, y: draftKeypoint.y }
          : spec.start,
      end: draftKeypoint?.name === spec.end.name ? { ...spec.end, x: draftKeypoint.x, y: draftKeypoint.y } : spec.end,
    }));

    // An endpoint the USER dragged off the AI mask is flagged so they know the
    // measurement is no longer anchored to the detected object. Scoped to
    // user-moved endpoints (live draft or a recorded adjustment) because the
    // model's own extreme-point keypoints legitimately sit on the mask boundary
    // and would otherwise warn on pristine captures.
    const isEndpointOffMask = (name: MeasurementKeypointName, point?: { x: number; y: number }) =>
      Boolean(
        endpointsEditable &&
          maskGrid &&
          bbox &&
          point &&
          (draftKeypoint?.name === name || isUserAdjustedMeasurementKeypoint(annotation, name)) &&
          !isDisplayPointInMask(point, maskGrid, bbox)
      );

    const liveMeasurements = useMemo(() => {
      if (!measurements || !draftKeypoint || draftKeypoint.valueCm == null) return measurements;
      return withMeasurementAxisValue(measurements, draftKeypoint.axisKey, draftKeypoint.valueCm / 100);
    }, [draftKeypoint, measurements]);
    const measurementLabels = liveMeasurements ? buildAnnotationMeasurementLabels(liveMeasurements) : [];
    const measurementLabelLayout = buildMeasurementLabelLayout(measurementLabels, LABEL_FONT_SIZE, LABEL_PADDING);

    const activeBBox = draftBBox ?? bbox;
    const x = activeBBox?.x ?? 0;
    const y = activeBBox?.y ?? 0;
    const width = activeBBox?.width ?? 0;
    const height = activeBBox?.height ?? 0;
    const isManual = isManualBBoxAnnotation(annotation);
    const boxStroke = outlineColor ?? color;
    const outsideBoundaryMaskOpacity =
      outsideBoundaryCandidateState === "pending" ? 0.55 : outsideBoundaryCandidateState === "rejected" ? 0 : 1;
    const emphasisStroke = selected ? "#facc15" : showHoverPreview ? "#ffffff" : undefined;
    const boxOutlineWidth = BOX_OUTLINE_WIDTH / totalScale;
    const centerX = x + width / 2;
    const centerY = y + height / 2;
    const handleRadius = Math.max(8 / totalScale, 5);
    /** Corner handles exist only on the manual box the user has selected in edit mode. */
    const resizeHandlesShown = Boolean(isVisible && isEditMode && isManual && selected);
    // Escape, deselect, or hiding the box takes the handles away with the pointer still
    // over one, and Konva fires no leave for an unmounted node. Without this the corner
    // stays highlighted for the next selection and the stage keeps a `grab` cursor the
    // user cannot shake off.
    useEffect(() => {
      if (resizeHandlesShown) return;
      setHoveredResizeCorner(null);
      if (cursorContainerRef.current) cursorContainerRef.current.style.cursor = "";
    }, [resizeHandlesShown]);
    // Deleting a selected box unmounts this viewer outright, so the effect above never
    // gets its false pass. Hand the cursor back on the way out or the stage keeps a
    // `grab` with nothing left to grab.
    useEffect(
      () => () => {
        if (cursorContainerRef.current) cursorContainerRef.current.style.cursor = "";
      },
      []
    );

    const stopEvent = (e: KonvaEventObject<MouseEvent | DragEvent>) => {
      e.cancelBubble = true;
    };

    // A listening handle (measurement endpoint, corner, manual body) swallows the
    // Stage's dblclick, so a double-click aimed at removing the box would otherwise do
    // nothing. Route the pointer to the parent's closest-to-center toggle so a
    // double-click anywhere over a box removes it — even on a handle sitting on top.
    const handleToggleDblClick = (e: KonvaEventObject<MouseEvent>) => {
      e.cancelBubble = true;
      // getRelativePointerPosition() is in the Layer's local (image) coordinates —
      // the same space annotation bboxes and AnnotationViewerBase.getImagePoint use.
      const point = e.target.getLayer()?.getRelativePointerPosition();
      if (point) onToggleAtPoint?.(point);
    };

    const setDraftBBox = (nextBBox: BBoxPixels | null) => {
      draftBBoxRef.current = nextBBox;
      setDraftBBoxState(nextBBox);
    };

    const selectAnnotation = (e: KonvaEventObject<MouseEvent | DragEvent>) => {
      if (!isEditMode) return;
      stopEvent(e);
      onSelect?.();
    };

    const clampBBox = (nextBBox: BBoxPixels): BBoxPixels => {
      const nextWidth = Math.max(MIN_BOX_SIZE, Math.min(nextBBox.width, rawWidth));
      const nextHeight = Math.max(MIN_BOX_SIZE, Math.min(nextBBox.height, rawHeight));
      return {
        x: Math.max(0, Math.min(nextBBox.x, rawWidth - nextWidth)),
        y: Math.max(0, Math.min(nextBBox.y, rawHeight - nextHeight)),
        width: nextWidth,
        height: nextHeight,
      };
    };

    // Neither wire channel can persist a translation while depth is unresolved:
    // measurementEdits needs a value we cannot compute, and keypointPositionEdits
    // is rejected outright for a depth capture. Moving anyway would look saved
    // and come back wrong, so a landmark-bearing box waits. Boxes with no axes
    // are unaffected.
    const boxGeometryLocked = measurementEditing.kind === "locked" && measurementLineSpecs.length > 0;
    /** A locked capture cannot be resized, so it must not advertise a grab. */
    const resizeCornersGrabbable = !boxGeometryLocked;

    const moveBBox = (e: KonvaEventObject<DragEvent>) => {
      if (!bbox || !isManual || !selected || !onBBoxChange || boxGeometryLocked) return;
      stopEvent(e);
      const nextBBox = clampBBox({ ...bbox, x: e.target.x(), y: e.target.y() });
      e.target.position({ x: nextBBox.x, y: nextBBox.y });
      setDraftBBox(nextBBox);
    };

    /** True when BOTH endpoints moved by the box delta, i.e. nothing was clamped. */
    const movedRigidly = (
      spec: { start: { x: number; y: number }; end: { x: number; y: number } },
      moved: { start: { x: number; y: number }; end: { x: number; y: number } },
      change: { kind: "translate"; dx: number; dy: number } | { kind: "resize"; next: BBoxPixels }
    ): boolean =>
      change.kind === "translate" &&
      moved.start.x === spec.start.x + change.dx &&
      moved.start.y === spec.start.y + change.dy &&
      moved.end.x === spec.end.x + change.dx &&
      moved.end.y === spec.end.y + change.dy;

    const commitDraftBBox = (e: KonvaEventObject<DragEvent>) => {
      if (!isManual || !selected || !onBBoxChange || boxGeometryLocked) return;
      stopEvent(e);
      const nextBBox = draftBBoxRef.current;
      setDraftBBox(null);
      if (!nextBBox || !bbox) return;

      const change = classifyBoxChange(bbox, nextBBox);
      if (!change) {
        onBBoxChange(nextBBox, { keypoints: keypoints ?? [], axisValuesM: {} });
        return;
      }

      // Only complete axes can travel: neither wire channel can carry a lone
      // endpoint, so an orphan landmark is left exactly where it was.
      const movedByName = new Map<string, { x: number; y: number }>();
      const axisValuesM: Record<string, number | null> = {};
      for (const [specIndex, spec] of measurementLineSpecs.entries()) {
        const moved = transformAxisForBoxChange(
          { start: { x: spec.start.x, y: spec.start.y }, end: { x: spec.end.x, y: spec.end.y } },
          change,
          seedFallbackFor(spec.key, specIndex),
          { width: rawWidth, height: rawHeight }
        );
        if (spec.start.name) movedByName.set(spec.start.name, moved.start);
        if (spec.end.name) movedByName.set(spec.end.name, moved.end);

        // An enlarge, or an edge dragged away from the landmarks, leaves this
        // axis untouched. Clearing its measurement then would destroy a value
        // for a shape nobody changed.
        const geometryChanged =
          moved.start.x !== spec.start.x ||
          moved.start.y !== spec.start.y ||
          moved.end.x !== spec.end.x ||
          moved.end.y !== spec.end.y;

        if (measurementEditing.kind === "3d") {
          if (geometryChanged) {
            // The endpoints now sample different depth, so the stored distance
            // no longer describes them. A failed recompute clears, not lies.
            // The mask travels with the box, so measure against it at the box's
            // NEW position — a context built from the old bbox describes a
            // neighborhood the saved annotation will no longer have.
            const movedMaskContext = maskGrid ? { grid: maskGrid, bbox: nextBBox } : null;
            const recomputedCm = measureDistanceCm(moved.start, moved.end, measurementEditing.depthFrame, {
              mask: movedMaskContext,
            });
            axisValuesM[spec.key] =
              recomputedCm !== null && Number.isFinite(recomputedCm) && recomputedCm > 0 ? recomputedCm / 100 : null;
          }
        } else if (geometryChanged && !movedRigidly(spec, moved, change)) {
          // 2D: the axis changed shape and nothing can re-measure it. A rigid
          // translate keeps its geometry — and its value — so it sends nothing.
          // Clamping at an image edge can break rigidity even on a translate,
          // and the server clears those, so the client must agree or its saved
          // aggregates go stale until reload.
          axisValuesM[spec.key] = null;
        }
      }

      const nextKeypoints = (keypoints ?? []).map((keypoint) => {
        const moved = keypoint.name ? movedByName.get(keypoint.name) : undefined;
        return moved ? { ...keypoint, ...moved } : keypoint;
      });

      onBBoxChange(nextBBox, { keypoints: nextKeypoints, axisValuesM });
    };

    const resizeBBox = (corner: ResizeCorner, e: KonvaEventObject<DragEvent>) => {
      if (!bbox || !isManual || !selected || !onBBoxChange || boxGeometryLocked) return;
      stopEvent(e);
      const sourceBBox = draftBBoxRef.current ?? bbox;
      const right = sourceBBox.x + sourceBBox.width;
      const bottom = sourceBBox.y + sourceBBox.height;
      const pointerX = Math.max(0, Math.min(e.target.x(), rawWidth));
      const pointerY = Math.max(0, Math.min(e.target.y(), rawHeight));

      const nextBBox = (() => {
        switch (corner) {
          case "nw": {
            const nextX = Math.min(pointerX, right - MIN_BOX_SIZE);
            const nextY = Math.min(pointerY, bottom - MIN_BOX_SIZE);
            return { x: nextX, y: nextY, width: right - nextX, height: bottom - nextY };
          }
          case "ne": {
            const nextY = Math.min(pointerY, bottom - MIN_BOX_SIZE);
            const nextRight = Math.max(pointerX, sourceBBox.x + MIN_BOX_SIZE);
            return { x: sourceBBox.x, y: nextY, width: nextRight - sourceBBox.x, height: bottom - nextY };
          }
          case "se": {
            const nextRight = Math.max(pointerX, sourceBBox.x + MIN_BOX_SIZE);
            const nextBottom = Math.max(pointerY, sourceBBox.y + MIN_BOX_SIZE);
            return {
              x: sourceBBox.x,
              y: sourceBBox.y,
              width: nextRight - sourceBBox.x,
              height: nextBottom - sourceBBox.y,
            };
          }
          case "sw": {
            const nextX = Math.min(pointerX, right - MIN_BOX_SIZE);
            const nextBottom = Math.max(pointerY, sourceBBox.y + MIN_BOX_SIZE);
            return { x: nextX, y: sourceBBox.y, width: right - nextX, height: nextBottom - sourceBBox.y };
          }
        }
      })();

      setDraftBBox(clampBBox(nextBBox));
    };

    const handlePositions: Array<{ corner: ResizeCorner; x: number; y: number }> = [
      { corner: "nw", x, y },
      { corner: "ne", x: x + width, y },
      { corner: "se", x: x + width, y: y + height },
      { corner: "sw", x, y: y + height },
    ];

    const clampToImage = (value: number, max: number) => Math.max(0, Math.min(value, max));

    const setStageCursor = (e: KonvaEventObject<MouseEvent | DragEvent>, cursor: string) => {
      const stage = e.target.getStage();
      if (!stage) return;
      const container = stage.container();
      // Remembered so a node that unmounts under the pointer can still put the cursor
      // back: no leave event fires in that case, and the inline style would stick.
      cursorContainerRef.current = container;
      container.style.cursor = cursor;
    };

    /**
     * The landmark this annotation should name because the sidebar legend points at
     * it. Resolved through the axis+role rather than the raw string, so a legend
     * built from a sibling that stores `length_start` still finds this annotation's
     * legacy `head`.
     */
    const resolveHighlightedName = () => {
      const wanted = highlightedKeypointName ? parseMeasurementEndpointName(highlightedKeypointName) : null;
      if (!wanted) return null;
      const spec = measurementLineSpecs.find((candidate) => candidate.key === wanted.key);
      return (wanted.endpoint === "start" ? spec?.start.name : spec?.end.name) ?? null;
    };

    // A hovered landmark can vanish out from under the cursor with no mouse-leave —
    // Ctrl+B collapses the boxes, the axis gets parked, the box stops being active.
    // Masking the stale name is not enough: it would resurrect the callout the moment
    // the landmark came back, with the cursor somewhere else entirely.
    const hoveredAxisHidden = Boolean(
      hoveredKeypointName && isAxisHidden(findEndpointByName(measurementLineSpecs, hoveredKeypointName)?.spec.key ?? "")
    );
    useEffect(() => {
      if (!endpointsIdentifiable || hoveredAxisHidden) setHoveredKeypointName(null);
    }, [endpointsIdentifiable, hoveredAxisHidden]);

    // Hover wins over the legend: the cursor is the more immediate intent.
    const calloutName = (endpointsIdentifiable ? hoveredKeypointName : null) ?? resolveHighlightedName();

    /** An on-screen pixel size in Layer units, held constant as the stage zooms. */
    const screenPx = (px: number) => zoomAdjustedLayerSize(px, fitScale, totalScale, 0);

    // One screen-stable value for BOTH the Konva hit region and the callout's
    // "is another landmark stacked here?" test, so the two cannot drift apart.
    const handleHitStrokeWidth = Math.max(screenPx(HANDLE_HIT_STROKE_PX), HANDLE_HIT_STROKE_MIN_PX);

    const findAxisForEndpoint = (name: MeasurementKeypointName) =>
      measurementLineSpecs.find((spec) => spec.start.name === name || spec.end.name === name);

    const axisEndpoints = (
      name: MeasurementKeypointName,
      point: { x: number; y: number }
    ): [{ x: number; y: number }, { x: number; y: number }] | null => {
      const spec = findAxisForEndpoint(name);
      if (!spec) return null;
      return spec.start.name === name ? [point, spec.end] : [spec.start, point];
    };

    const moveEndpoint = (name: MeasurementKeypointName, e: KonvaEventObject<DragEvent>) => {
      stopEvent(e);
      const spec = findAxisForEndpoint(name);
      if (!spec || measurementEditing.kind === "locked") return;
      const nextX = clampToImage(e.target.x(), rawWidth);
      const nextY = clampToImage(e.target.y(), rawHeight);
      e.target.position({ x: nextX, y: nextY });
      const endpoints = axisEndpoints(name, { x: nextX, y: nextY });
      // Without depth there is no physical distance to report — only the on-screen
      // separation, which still tells the user how the axis is growing as they drag.
      const valueCm =
        endpoints && measurementEditing.kind === "3d"
          ? measureDistanceCm(endpoints[0], endpoints[1], measurementEditing.depthFrame, { mask: maskContext })
          : null;
      const pixelLengthPx =
        endpoints && measurementEditing.kind === "2d"
          ? Math.hypot(endpoints[1].x - endpoints[0].x, endpoints[1].y - endpoints[0].y)
          : undefined;
      setDraftKeypoint({ name, axisKey: spec.key, x: nextX, y: nextY, valueCm });
      const identity = describeMeasurementLineEndpoint(
        spec,
        spec.start.name === name ? "start" : "end",
        measurementDescriptors
      );
      onMeasurementDraft?.({
        annotationId: annotation.id,
        keypointName: name,
        axisKey: spec.key,
        label: identity.label,
        color: identity.color,
        valueCm,
        ...(pixelLengthPx !== undefined ? { pixelLengthPx } : {}),
        x: nextX,
        y: nextY,
        bbox,
      });
    };

    const commitEndpoint = (name: MeasurementKeypointName, e: KonvaEventObject<DragEvent>) => {
      stopEvent(e);
      const draft = draftKeypointRef.current;
      setDraftKeypoint(null);
      onMeasurementDraft?.(null);
      if (!draft || !onMeasurementKeypointChange) return;
      // 2D commits geometry only: there is no value to recompute, so there is
      // nothing that can fail and nothing to snap back from.
      if (measurementEditing.kind === "2d") {
        onMeasurementKeypointChange(name, draft.x, draft.y, null);
        return;
      }
      const endpoints = axisEndpoints(name, { x: draft.x, y: draft.y });
      const valueCm =
        measurementEditing.kind === "3d" && endpoints
          ? measureDistanceCm(endpoints[0], endpoints[1], measurementEditing.depthFrame, { mask: maskContext })
          : null;
      if (valueCm == null || !Number.isFinite(valueCm) || valueCm <= 0) {
        onInvalidMeasurement?.();
        return;
      }
      onMeasurementKeypointChange(name, draft.x, draft.y, valueCm / 100);
    };

    const renderEndpoint = (spec: (typeof activeMeasurementLineSpecs)[number], endpoint: "start" | "end") => {
      const point = spec[endpoint];
      const name = point.name;
      if (!name) return null;
      const axisHidden = isAxisHidden(spec.key);
      // Lazy: preview never reads it, and this runs once per endpoint per annotation.
      const endpointColor = () => describeMeasurementLineEndpoint(spec, endpoint, measurementDescriptors).color;

      // Default (box not active for editing): the original subtle dot — thin, no contrasting ring,
      // non-interactive. Restores the pre-emphasis look everywhere except the box being worked on.
      if (!isActiveForEditing) {
        return (
          <Circle
            key={name}
            name={name}
            x={point.x}
            y={point.y}
            opacity={axisHidden ? HIDDEN_AXIS_OPACITY : 1}
            radius={CIRCLE_RADIUS / totalScale}
            fill={endpointsColorCoded ? endpointColor() : spec.color}
            listening={false}
            perfectDrawEnabled={false}
          />
        );
      }

      // No stored value required: a hand-drawn box's first measurement has to be
      // draggable before it can ever have one.
      const axisEditable = endpointsEditable && !axisHidden;
      // A PARKED axis is the one that must stay fully deaf: being non-listening is
      // exactly what frees a coincident handle underneath it to be grabbed.
      const axisIdentifiable = endpointsIdentifiable && !axisHidden;
      const offMask = isEndpointOffMask(name, point);
      const dotFill = offMask ? "#f97316" : endpointColor();
      // `chrome` maps an on-screen px size into Layer coordinates: draggable handles stay a fixed size
      // (growth 0, screen-constant); the non-draggable dots use the helper's gentle default growth.
      const chrome = (px: number) =>
        axisEditable
          ? zoomAdjustedLayerSize(px, fitScale, totalScale, 0)
          : zoomAdjustedLayerSize(px, fitScale, totalScale);
      const warnRadius = chrome(CIRCLE_RADIUS + 5);
      return (
        <React.Fragment key={name}>
          <Circle
            name={name}
            x={point.x}
            y={point.y}
            opacity={axisHidden ? HIDDEN_AXIS_OPACITY : 1}
            radius={chrome(axisEditable ? ACTIVE_HANDLE_RADIUS_PX : ACTIVE_KEYPOINT_RADIUS_PX)}
            fill={dotFill}
            // A contrasting ring keeps every dot legible over any mask color, not just the edit handles.
            stroke={axisEditable ? "white" : getContrastingOutlineColor(dotFill)}
            strokeWidth={chrome(axisEditable ? ACTIVE_HANDLE_STROKE_PX : ACTIVE_KEYPOINT_STROKE_PX)}
            {...grabbableCasing(axisEditable, chrome(5))}
            draggable={axisEditable}
            listening={axisIdentifiable}
            // Every handler below follows `listening`, not `draggable`. A listening
            // node with no `onMouseDown` takes the selected manual box's body rect
            // out of the hit path and lets the press bubble to the Stage, which
            // clears the selection and opens a draft box; a missing `onDblClick`
            // likewise turns the dot into a dead zone for the visibility toggle.
            // `selectAnnotation` no-ops outside edit mode, and a dot only reaches
            // this branch when its box is active, which implies edit mode.
            hitStrokeWidth={axisIdentifiable ? handleHitStrokeWidth : 0}
            perfectDrawEnabled={false}
            onMouseDown={axisIdentifiable ? selectAnnotation : undefined}
            onDblClick={axisIdentifiable ? handleToggleDblClick : undefined}
            onMouseEnter={
              axisIdentifiable
                ? (e) => {
                    setStageCursor(e, axisEditable ? "grab" : "default");
                    setHoveredKeypointName(name);
                  }
                : undefined
            }
            onMouseLeave={
              axisIdentifiable
                ? (e) => {
                    setStageCursor(e, "");
                    setHoveredKeypointName((current) => (current === name ? null : current));
                  }
                : undefined
            }
            onDragStart={
              axisEditable
                ? (e) => {
                    stopEvent(e);
                    setStageCursor(e, "grabbing");
                  }
                : undefined
            }
            onDragMove={axisEditable ? (e) => moveEndpoint(name, e) : undefined}
            onDragEnd={
              axisEditable
                ? (e) => {
                    setStageCursor(e, "grab");
                    commitEndpoint(name, e);
                  }
                : undefined
            }
          />
          {offMask && (
            // "!" badge above the endpoint: this point is off the detected object.
            <>
              <Circle
                x={point.x}
                y={point.y - warnRadius * 2}
                radius={warnRadius}
                fill="#f97316"
                stroke="white"
                strokeWidth={chrome(1.5)}
                listening={false}
                perfectDrawEnabled={false}
              />
              <Text
                x={point.x - warnRadius}
                y={point.y - warnRadius * 3}
                width={warnRadius * 2}
                height={warnRadius * 2}
                text="!"
                fontSize={chrome(CIRCLE_RADIUS + 2)}
                fontStyle="bold"
                fill="white"
                align="center"
                verticalAlign="middle"
                listening={false}
              />
            </>
          )}
        </React.Fragment>
      );
    };

    /**
     * The name callout for one landmark: an emphasis ring plus a chip carrying the
     * friendly label, the persisted keypoint name, and — when a second landmark sits
     * under the same pixel — which one is buried there.
     *
     * Drawn in its own pass AFTER every endpoint so a later axis can never paint over
     * it, and entirely non-listening so it cannot intercept the drag it is describing.
     */
    const renderLandmarkCallout = (calloutName: string) => {
      const found = findEndpointByName(activeMeasurementLineSpecs, calloutName);
      if (!found || isAxisHidden(found.spec.key)) return null;

      const { spec, endpoint } = found;
      const point = spec[endpoint];
      const identity = describeMeasurementLineEndpoint(spec, endpoint, measurementDescriptors);

      // Any other landmark within the same grab radius is unreachable while this one
      // is on top — the bisected-lettuce seed puts `length_start` and
      // `core_length_start` on the identical pixel — so name it rather than leave the
      // labeler with an invisible point.
      const stackRadius = handleHitStrokeWidth / 2;
      const buried = activeMeasurementLineSpecs.flatMap((candidate) =>
        (["start", "end"] as const)
          .filter((role) => {
            const other = candidate[role];
            if (other.name === calloutName || !other.name || isAxisHidden(candidate.key)) return false;
            return Math.hypot(other.x - point.x, other.y - point.y) <= stackRadius;
          })
          .map((role) => describeMeasurementLineEndpoint(candidate, role, measurementDescriptors).label)
      );

      const titleSize = screenPx(CALLOUT_TITLE_FONT_PX);
      const subtitleSize = screenPx(CALLOUT_SUBTITLE_FONT_PX);
      const padding = screenPx(CALLOUT_PADDING_PX);
      const lineGap = screenPx(CALLOUT_LINE_GAP_PX);
      const texts = [
        { text: identity.label, fontSize: titleSize, fill: "#ffffff", fontStyle: "bold" },
        { text: identity.name, fontSize: subtitleSize, fill: CALLOUT_SUBTITLE_COLOR },
        ...buried.map((label) => ({
          text: `also here: ${label}`,
          fontSize: subtitleSize,
          fill: CALLOUT_SUBTITLE_COLOR,
        })),
      ];

      const width =
        Math.max(...texts.map((line) => line.text.length * line.fontSize * MEASUREMENT_LABEL_CHAR_WIDTH_RATIO)) +
        padding * 2;
      const height =
        texts.reduce((total, line) => total + line.fontSize, 0) + lineGap * (texts.length - 1) + padding * 2;

      // Place against what is ON SCREEN, not against the image. Once the stage is
      // zoomed the image extends far past the clipped viewport, so an "inside the
      // image" flip leaves the chip unreadable exactly when the labeler is zoomed in
      // to work precisely. Falls back to the whole image when the viewport is
      // unknown (which is what it is at fit scale anyway).
      const visible = getVisibleImageRect?.() ?? { x: 0, y: 0, width: rawWidth, height: rawHeight };
      const offset = screenPx(CALLOUT_OFFSET_PX);
      const clampInto = (value: number, min: number, extent: number, size: number) =>
        Math.max(min, Math.min(value, min + Math.max(extent - size, 0)));
      // Prefer the right of the dot; flip left when that would overflow. Clamp after
      // flipping, so a chip wider than the visible strip still starts on screen.
      const preferred =
        point.x + offset + width > visible.x + visible.width ? point.x - offset - width : point.x + offset;
      const x = clampInto(preferred, visible.x, visible.width, width);
      const y = clampInto(point.y - height / 2, visible.y, visible.height, height);

      // Stack the lines up front so the JSX below stays a pure map.
      let nextLineY = y + padding;
      const lines = texts.map((line) => {
        const lineY = nextLineY;
        nextLineY += line.fontSize + lineGap;
        return { ...line, y: lineY };
      });

      return (
        <React.Fragment key={`${calloutName}-callout`}>
          <Rect
            x={x}
            y={y}
            width={width}
            height={height}
            fill={CALLOUT_BACKGROUND}
            cornerRadius={screenPx(CALLOUT_CORNER_RADIUS_PX)}
            listening={false}
            perfectDrawEnabled={false}
          />
          {lines.map((line) => (
            <Text
              key={line.text}
              x={x + padding}
              y={line.y}
              width={width - padding * 2}
              text={line.text}
              fontSize={line.fontSize}
              fontStyle={line.fontStyle}
              fill={line.fill}
              listening={false}
              perfectDrawEnabled={false}
            />
          ))}
          {/* Drawn last so the ring reads as the topmost thing on the canvas. */}
          <Circle
            name={`${calloutName}-highlight`}
            x={point.x}
            y={point.y}
            radius={screenPx(ACTIVE_HANDLE_RADIUS_PX + CALLOUT_RING_EXTRA_PX)}
            stroke={identity.color}
            strokeWidth={screenPx(CALLOUT_RING_STROKE_PX)}
            fillEnabled={false}
            listening={false}
            perfectDrawEnabled={false}
          />
        </React.Fragment>
      );
    };

    useEffect(() => {
      if (!selected || !isEditMode || !isVisible) {
        const hadMeasurementDraft = draftKeypointRef.current !== null;
        draftBBoxRef.current = null;
        setDraftBBoxState(null);
        draftKeypointRef.current = null;
        setDraftKeypointState(null);
        if (hadMeasurementDraft) onMeasurementDraft?.(null);
      }
    }, [annotation.id, isEditMode, isVisible, onMeasurementDraft, selected]);

    if (!bbox || !activeBBox) return null;

    // Collapse-to-points: draw only a center dot (no mask/box/lines/keypoints/labels), so
    // a crowded image is legible. Non-listening so a double-click still reaches the Stage
    // and toggles the box; the dot enlarges when it's the one a double-click would remove.
    if (collapseToPoints) {
      if (!isVisible) return null;
      // Reuse the box's select/hover color treatment so the dot highlights identically.
      const radiusPx = selected || showHoverPreview ? COLLAPSE_POINT_ACTIVE_RADIUS_PX : COLLAPSE_POINT_RADIUS_PX;
      return (
        <Circle
          x={centerX}
          y={centerY}
          radius={zoomAdjustedLayerSize(radiusPx, fitScale, totalScale, 0)}
          fill={boxStroke}
          fillEnabled={outsideBoundaryCandidateState !== "rejected"}
          opacity={outsideBoundaryCandidateState ? 0.8 : 1}
          stroke={getContrastingOutlineColor(boxStroke)}
          strokeWidth={zoomAdjustedLayerSize(1.5, fitScale, totalScale, 0)}
          listening={false}
          perfectDrawEnabled={false}
        />
      );
    }

    return (
      <>
        {/* Segmentation mask — `showMask` is a view-only toggle (never affects downloads). */}
        {maskCanvas && (
          <Image
            image={maskCanvas.canvas}
            x={maskCanvas.x}
            y={maskCanvas.y}
            opacity={(isVisible || showHoverPreview) && showMask ? outsideBoundaryMaskOpacity : 0}
            listening={false}
          />
        )}

        {/* Box outline in edit mode, or in preview when there is no mask. */}
        {isVisible && (isEditMode || !maskCanvas) && (
          <>
            <Rect
              x={x}
              y={y}
              width={width}
              height={height}
              stroke={boxStroke}
              strokeWidth={boxOutlineWidth}
              opacity={outsideBoundaryCandidateState === "rejected" ? 0.45 : 1}
              dash={
                outsideBoundaryCandidateState === "pending"
                  ? [12 / totalScale, 8 / totalScale]
                  : outsideBoundaryCandidateState === "rejected"
                    ? [4 / totalScale, 8 / totalScale]
                    : isManual
                      ? undefined
                      : []
              }
              fillEnabled={false}
              listening={false}
              perfectDrawEnabled={false}
            />
            {emphasisStroke && (
              <Rect
                x={x}
                y={y}
                width={width}
                height={height}
                stroke={emphasisStroke}
                strokeWidth={BOX_OUTLINE_EMPHASIS_WIDTH / totalScale}
                dash={[10 / totalScale, 6 / totalScale]}
                fillEnabled={false}
                listening={false}
                perfectDrawEnabled={false}
              />
            )}
          </>
        )}

        {isVisible &&
          activeMeasurementLineSpecs.map((spec) => {
            const axisOpacity = isAxisHidden(spec.key) ? HIDDEN_AXIS_OPACITY : 1;
            const points = [spec.start.x, spec.start.y, spec.end.x, spec.end.y];
            // Geometry always draws; only its styling admits there is no number.
            const measured = isMeasurementAxisValued(annotation.measurements, spec.key);
            const lineColor = measured ? spec.color : UNMEASURED_AXIS_STROKE;
            const dash = measured ? undefined : UNMEASURED_AXIS_DASH.map((segment) => segment / totalScale);

            // Default: the original subtle single line, no casing.
            if (!isActiveForEditing) {
              return (
                <Line
                  key={spec.key}
                  points={points}
                  stroke={lineColor}
                  dash={dash}
                  opacity={axisOpacity}
                  strokeWidth={AXIS_LINE_WIDTH / totalScale}
                  listening={false}
                  perfectDrawEnabled={false}
                />
              );
            }

            // Active for editing: contrasting casing + colored line, restrained thickness.
            return (
              <React.Fragment key={spec.key}>
                {/* Contrasting casing beneath the colored line so it never blends into the mask. */}
                <Line
                  points={points}
                  stroke={getContrastingOutlineColor(lineColor)}
                  opacity={axisOpacity}
                  strokeWidth={zoomAdjustedLayerSize(
                    ACTIVE_AXIS_LINE_BASE_PX + ACTIVE_AXIS_LINE_CASING_EXTRA_PX,
                    fitScale,
                    totalScale
                  )}
                  lineCap="round"
                  listening={false}
                  perfectDrawEnabled={false}
                />
                <Line
                  points={points}
                  stroke={lineColor}
                  dash={dash}
                  opacity={axisOpacity}
                  strokeWidth={zoomAdjustedLayerSize(ACTIVE_AXIS_LINE_BASE_PX, fitScale, totalScale)}
                  // Round caps would grow each dash into its gaps at this thickness.
                  lineCap={dash ? "butt" : "round"}
                  listening={false}
                  perfectDrawEnabled={false}
                />
              </React.Fragment>
            );
          })}

        {/* Measurement label */}
        {isVisible && measurementLabels.length > 0 && showText && (
          <>
            <Rect
              x={centerX}
              y={centerY + height / 2 + 50}
              offsetX={measurementLabelLayout.width / totalScale / 2}
              offsetY={measurementLabelLayout.height / totalScale / 2}
              width={measurementLabelLayout.width / totalScale}
              height={measurementLabelLayout.height / totalScale}
              fill="rgba(0, 0, 0, 0.8)"
              cornerRadius={4 / totalScale}
              listening={false}
            />
            <Text
              x={centerX}
              y={centerY + height / 2 + 50}
              offsetX={(measurementLabelLayout.width - LABEL_PADDING * 2) / totalScale / 2}
              offsetY={(measurementLabelLayout.height - LABEL_PADDING * 2) / totalScale / 2}
              width={(measurementLabelLayout.width - LABEL_PADDING * 2) / totalScale}
              height={(measurementLabelLayout.height - LABEL_PADDING * 2) / totalScale}
              text={measurementLabelLayout.text}
              fontSize={LABEL_FONT_SIZE / totalScale}
              lineHeight={MEASUREMENT_LABEL_LINE_HEIGHT}
              fill="white"
              stroke="black"
              strokeWidth={1 / totalScale}
              align="center"
              verticalAlign="middle"
              listening={false}
            />
          </>
        )}

        {/* The ONLY listening surface a box body ever gets, and only to drag a selected
            manual box. Model boxes get none: selection is resolved from the Stage by
            closest-to-center, so a listening body would do nothing but swallow drags
            aimed at a neighbour's measurement handles. */}
        {isVisible && isEditMode && isManual && selected && (
          <Rect
            x={x}
            y={y}
            width={width}
            height={height}
            fill="rgba(255,255,255,0.001)"
            fillEnabled
            stroke="transparent"
            strokeWidth={1 / totalScale}
            hitStrokeWidth={Math.max(24 / totalScale, 10)}
            // Konva moves the node itself on drag, so an ignored handler still
            // leaves the hit target detached from the box. Refuse the drag.
            draggable={!boxGeometryLocked}
            // Keep this box selected: its body IS the drag handle, so re-resolving
            // selection here would make an overlapped manual box impossible to move.
            onMouseDown={selectAnnotation}
            onDragMove={moveBBox}
            onDragStart={stopEvent}
            onDragEnd={commitDraftBBox}
            onDblClick={handleToggleDblClick}
          />
        )}

        {resizeHandlesShown &&
          handlePositions.map((handle) => {
            const hovered = resizeCornersGrabbable && hoveredResizeCorner === handle.corner;
            return (
              <Circle
                key={handle.corner}
                name={`resize-${handle.corner}`}
                x={handle.x}
                y={handle.y}
                // Grow the corner under the cursor and give it the same dark casing a
                // grabbable landmark gets, so "this is the one you'd drag" reads the
                // same way everywhere in the editor.
                radius={hovered ? handleRadius * RESIZE_HANDLE_HOVER_GROWTH : handleRadius}
                fill="#facc15"
                stroke="white"
                strokeWidth={(hovered ? RESIZE_HANDLE_HOVER_RING_WIDTH : RESIZE_HANDLE_RING_WIDTH) / totalScale}
                {...grabbableCasing(hovered, handleRadius * RESIZE_HANDLE_HOVER_CASING)}
                draggable={resizeCornersGrabbable}
                onMouseDown={selectAnnotation}
                onClick={selectAnnotation}
                onDblClick={handleToggleDblClick}
                onMouseEnter={
                  resizeCornersGrabbable
                    ? (e) => {
                        setStageCursor(e, "grab");
                        setHoveredResizeCorner(handle.corner);
                      }
                    : undefined
                }
                onMouseLeave={
                  resizeCornersGrabbable
                    ? (e) => {
                        setStageCursor(e, "");
                        // Only clear if this corner is still the hovered one: a
                        // collapsed box stacks all four, so enter/leave can interleave.
                        setHoveredResizeCorner((current) => (current === handle.corner ? null : current));
                      }
                    : undefined
                }
                onDragMove={(e) => resizeBBox(handle.corner, e)}
                onDragStart={(e) => {
                  stopEvent(e);
                  setStageCursor(e, "grabbing");
                }}
                onDragEnd={(e) => {
                  setStageCursor(e, "grab");
                  commitDraftBBox(e);
                }}
              />
            );
          })}

        {isVisible &&
          activeMeasurementLineSpecs.flatMap((spec) => [renderEndpoint(spec, "start"), renderEndpoint(spec, "end")])}

        {/* Separate pass, after every landmark of THIS annotation, so no axis of its
            own can paint over the callout. Known limit: the parent draws later
            annotations after this one, so on a crowded capture a neighbour's mask or
            box can still overlap the chip. Fixing that needs the callout lifted into
            a container-level final pass above all viewers. */}
        {isVisible && calloutName && renderLandmarkCallout(calloutName)}
      </>
    );
  }
);
